import React, { useMemo } from 'react'
import { Container, Box, Typography, Select, MenuItem } from '@mui/material'
import NavBreadcrumbs from '@layout/Breadcrumbs'
import AppSidebar from '@layout/AppSidebar'
import { useIsMobile } from '@/hooks/Responsive'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from "@components/AuthContext";

export default function PageContainer({
  children,
  sidebar = <AppSidebar items={[]} />,
  title = '',
  hideHeader = false,
  items = [],
  contentMinWidth = false,
  contentMaxWidth = false,
}) {
  const hasSidebar = Boolean(sidebar)
  const { isMobile } = useIsMobile()
  const navigate = useNavigate()
  const location = useLocation()

  /* ---------------------------------------------
   * depth 기준 current 계산 (PC/공통용)
   * --------------------------------------------- */
  function findDepthCurrent(depthItems, pathname) {
    const norm = pathname.split(/[?#]/)[0]
    let matched = null
    let longest = 0

    for (const it of depthItems) {
      if (!it.href) continue
      if (norm === it.href || norm.startsWith(it.href + '/')) {
        if (it.href.length > longest) {
          matched = it
          longest = it.href.length
        }
      }
    }
    return matched
  }

  /* ---------------------------------------------
   * 모바일 Select 전용 value 결정 로직
   * --------------------------------------------- */
  function resolveSelectValue(pathname, depthItems) {
    const norm = pathname.split(/[?#]/)[0]

    // 마지막 path segment (ex: account)
    const lastSegment = norm.split('/').pop()

    // 1. 정확히 일치
    const exact = depthItems.find(it => it.href === norm)
    if (exact) return exact.href

    // 2. 마지막 세그먼트 기준 의미 매칭
    const segmentMatch = depthItems.find(it =>
      it.href.split('/').pop().includes(lastSegment)
    )
    if (segmentMatch) return segmentMatch.href

    // 3. 최후 fallback
    return depthItems[0]?.href ?? ''
  }

  const sidebarItems = sidebar?.props?.items || []
  const sourceItems = items.length ? items : sidebarItems

  // professorOnly 필터링
  const { user } = useAuth()
  const clss = user?.clss ?? ''

  const isProfGroup =
    clss === 'PROFESSOR' ||
    clss === 'ASSISTANT'

  const filteredItems = useMemo(() => {
    const filterRecursive = (arr) =>
      arr
        .map(item => {
          if (!isProfGroup && item.professorOnly) return null
          if (isProfGroup && item.noProfessor) return null
          const children = item.children ? filterRecursive(item.children) : []
          return { ...item, children }
        })
        .filter(Boolean)

    const result = filterRecursive(sourceItems)

    if (sourceItems.__rootLabel) {
      result.__rootLabel = sourceItems.__rootLabel
    }

    return result
  }, [sourceItems, isProfGroup])

  const depthSource = filteredItems

  // hidden은 모바일에서도 제외 (지금 구조 기준)
  const depthItems = useMemo(
    () => depthSource.filter(it => !it.hidden),
    [depthSource]
  )

  const current = findDepthCurrent(depthSource, location.pathname)

  const selectValue = isMobile
    ? resolveSelectValue(location.pathname, depthItems)
    : current?.href ?? ''

  const handleChange = (e) => {
    navigate(e.target.value)
  }

  /* ---------------------------------------------
   * 헤더/사이드바 없는 경우
   * --------------------------------------------- */
  if (hideHeader && !hasSidebar) {
    return (
      <Box sx={{ width: '100%', py: isMobile ? 8 : 15 }}>
        {children}
      </Box>
    )
  }

  const headerHeight = isMobile ? '64px' : '120px'

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        paddingBlockStart: headerHeight,
        mt: isMobile ? 4 : 8,
        mb: isMobile ? 8 : 15,
        display: 'flex',
        gap: isMobile ? 0 : 12,
        px: 2,
      }}
    >
      {/* 사이드바 */}
      {hasSidebar && (
        <Box
          component="aside"
          sx={{
            display: { xs: 'none', xl: 'block' },
            width: '240px',
            flexShrink: 0,
          }}
        >
          {sidebar}
        </Box>
      )}

      {/* 메인 Section */}
      <Box component="section" sx={{ flex: 1 }}>
        {!hideHeader && (
          <>
            <NavBreadcrumbs />

            {/* 모바일: Select */}
            {!contentMinWidth && !contentMaxWidth && isMobile ? (
              <Select
                value={selectValue}
                onChange={handleChange}
                fullWidth
                variant="standard"
                disableUnderline
                sx={{
                  mt: 2,
                  mb: 1,
                  typography: 'h6',
                  '& .MuiSvgIcon-root': { right: 0 },
                }}
              >
                {depthItems.map((it) => (
                  <MenuItem key={it.href} value={it.href}>
                    {it.label}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography
                component="h3"
                sx={{
                  mt: isMobile ? 2 : 3,
                  mb: isMobile ? 4 : 6,
                  typography: isMobile ? 'h6' : 'h5',
                  display: contentMinWidth ? 'none' : 'block',
                }}
              >
                {title}
              </Typography>
            )}
          </>
        )}

        {/* 콘텐츠 영역 */}
        <Box
          sx={{
            width: '100%',
            maxWidth: (() => {
              if (!isMobile && contentMinWidth) return '720px'
              if (!isMobile && contentMaxWidth) return '1400px'
              return '100%'
            })(),
            margin: '0 auto',
            mt: hideHeader ? 0 : 3,
          }}
        >
          {children}
        </Box>
      </Box>
    </Container>
  )
}
